import React, { useState, useEffect } from 'react';
import { onboardingConfig } from '../../onboarding/onboardingConfig';
import { useAuth } from '../../contexts/AuthContext';
import HelpIcon from './HelpIcon';

const UnifiedOnboardingWidget = ({ currentPhase, completedSteps = [], onStepClick, isMobile }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [justCompleted, setJustCompleted] = useState(false);
    const { updateOnboardingStatus } = useAuth();

    // Get current phase steps
    const phaseSteps = currentPhase === 'phase_1'
        ? onboardingConfig.phase1.checklist
        : onboardingConfig.phase2.checklist;

    const completedCount = phaseSteps.filter(step => completedSteps.includes(step.id)).length;
    const totalSteps = phaseSteps.length;
    const progress = (completedCount / totalSteps) * 100;
    const phaseLabel = currentPhase === 'phase_1' ? 'Phase 1' : 'Phase 2';

    // Trigger pulse animation when a step completes
    useEffect(() => {
        if (completedCount > 0) {
            setJustCompleted(true);
            const timer = setTimeout(() => setJustCompleted(false), 1000);
            return () => clearTimeout(timer);
        }
    }, [completedCount]);

    // Gradient colors based on phase
    const gradientColors = currentPhase === 'phase_1'
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' // Purple gradient for Phase 1
        : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'; // Green gradient for Phase 2

    const accentColor = currentPhase === 'phase_1' ? '#667eea' : '#11998e';

    // Widget styles
    const widgetContainerStyle = {
        position: 'fixed',
        bottom: isMobile ? '24px' : '24px',
        right: '24px',
        zIndex: 1500,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    };

    const widgetButtonStyle = {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '70px',
        height: '70px',
        borderRadius: '50%',
        background: gradientColors,
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        boxShadow: justCompleted
            ? '0 8px 30px rgba(102, 126, 234, 0.5)'
            : '0 4px 20px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.3s ease',
        fontSize: '14px',
        fontWeight: '700',
        overflow: 'visible',
        animation: justCompleted ? 'pulse 0.6s ease-out' : 'none',
    };

    const progressRingStyle = {
        position: 'absolute',
        top: '-4px',
        left: '-4px',
        width: '78px',
        height: '78px',
        transform: 'rotate(-90deg)',
    };

    const expandedPanelStyle = {
        position: 'absolute',
        bottom: '85px',
        right: 0,
        width: isMobile ? '90vw' : '380px',
        maxWidth: '380px',
        backgroundColor: '#fff',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
        padding: '24px',
        animation: 'slideUp 0.3s ease-out',
        maxHeight: '500px',
        overflowY: 'auto',
    };

    const headerStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
    };

    const titleStyle = {
        fontSize: '18px',
        fontWeight: '700',
        color: '#003366',
        margin: 0,
    };

    const closeButtonStyle = {
        background: 'none',
        border: 'none',
        fontSize: '24px',
        color: '#999',
        cursor: 'pointer',
        padding: 0,
        transition: 'color 0.2s ease',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const progressBarContainerStyle = {
        marginBottom: '20px',
    };

    const progressLabelStyle = {
        fontSize: '14px',
        color: '#666',
        marginBottom: '8px',
        fontWeight: '500',
    };

    const progressBarStyle = {
        width: '100%',
        height: '8px',
        backgroundColor: '#e0e0e0',
        borderRadius: '4px',
        overflow: 'hidden',
    };

    const progressFillStyle = {
        height: '100%',
        width: `${progress}%`,
        background: gradientColors,
        transition: 'width 0.3s ease',
        borderRadius: '4px',
    };

    const stepsListStyle = {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    };

    const stepItemStyle = (isCompleted) => ({
        display: 'flex',
        alignItems: 'center',
        padding: '14px 0',
        borderBottom: '1px solid #f0f0f0',
        fontSize: '14px',
        color: isCompleted ? accentColor : '#333',
        transition: 'all 0.2s ease',
    });

    const stepCheckboxStyle = (isCompleted) => ({
        width: '22px',
        height: '22px',
        borderRadius: '4px',
        backgroundColor: isCompleted ? accentColor : '#fff',
        border: `2px solid ${isCompleted ? accentColor : '#ddd'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: '12px',
        fontWeight: 'bold',
        flexShrink: 0,
        marginRight: '12px',
        transition: 'all 0.2s ease',
    });

    const skipButtonStyle = {
        width: '100%',
        padding: '12px',
        marginTop: '16px',
        backgroundColor: 'transparent',
        color: '#666',
        border: '1px solid #ddd',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    };

    const handleSkip = async () => {
        if (window.confirm('Are you sure you want to skip the onboarding? You can always access help resources later.')) {
            await updateOnboardingStatus('completed');
        }
    };

    // Calculate circle progress for SVG ring
    const circumference = 2 * Math.PI * 35; // radius = 35
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <div style={widgetContainerStyle}>
            {/* Expanded Panel */}
            {isExpanded && (
                <div style={expandedPanelStyle}>
                    <div style={headerStyle}>
                        <h3 style={titleStyle}>{phaseLabel}: Setup Progress</h3>
                        <button
                            style={closeButtonStyle}
                            onClick={() => setIsExpanded(false)}
                            onMouseEnter={(e) => (e.target.style.color = '#333')}
                            onMouseLeave={(e) => (e.target.style.color = '#999')}
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>

                    <div style={progressBarContainerStyle}>
                        <div style={progressLabelStyle}>
                            {completedCount} of {totalSteps} steps completed
                        </div>
                        <div style={progressBarStyle}>
                            <div style={progressFillStyle} />
                        </div>
                    </div>

                    <ul style={stepsListStyle}>
                        {phaseSteps.map((step, index) => {
                            const isCompleted = completedSteps.includes(step.id);
                            return (
                                <li
                                    key={step.id}
                                    style={stepItemStyle(isCompleted)}
                                    onClick={() => {
                                        if (!isCompleted && step.tour) {
                                            onStepClick(step.tour);
                                            setIsExpanded(false);
                                        }
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isCompleted) {
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            e.currentTarget.style.cursor = 'pointer';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <div style={stepCheckboxStyle(isCompleted)}>
                                        {isCompleted && '✓'}
                                    </div>
                                    <span style={{ flex: 1 }}>
                                        {index + 1}. {step.title}
                                    </span>
                                    <HelpIcon help={step.help} position="left" />
                                </li>
                            );
                        })}
                    </ul>

                    <button
                        style={skipButtonStyle}
                        onClick={handleSkip}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f8f9fa';
                            e.target.style.borderColor = '#999';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.borderColor = '#ddd';
                        }}
                    >
                        Skip Onboarding
                    </button>
                </div>
            )}

            {/* Floating Button */}
            <button
                style={widgetButtonStyle}
                onClick={() => setIsExpanded(!isExpanded)}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.08)';
                    e.currentTarget.style.boxShadow = '0 6px 25px rgba(0, 0, 0, 0.25)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = justCompleted
                        ? '0 8px 30px rgba(102, 126, 234, 0.5)'
                        : '0 4px 20px rgba(0, 0, 0, 0.15)';
                }}
                title={`${phaseLabel}: ${completedCount}/${totalSteps} steps completed`}
                aria-label={`Onboarding progress: ${completedCount} of ${totalSteps} completed`}
            >
                {/* Progress Ring */}
                <svg style={progressRingStyle}>
                    <circle
                        cx="39"
                        cy="39"
                        r="35"
                        stroke="rgba(255, 255, 255, 0.3)"
                        strokeWidth="3"
                        fill="none"
                    />
                    <circle
                        cx="39"
                        cy="39"
                        r="35"
                        stroke="#ffffff"
                        strokeWidth="3"
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                    />
                </svg>

                {/* Badge Content */}
                <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '2px' }}>
                    {phaseLabel}
                </div>
                <div style={{ fontSize: '18px', fontWeight: '700' }}>
                    {completedCount}/{totalSteps}
                </div>
            </button>

            <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.12);
          }
        }

        /* Scrollbar styling for expanded panel */
        div::-webkit-scrollbar {
          width: 6px;
        }
        div::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        div::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 3px;
        }
        div::-webkit-scrollbar-thumb:hover {
          background: #999;
        }
      `}</style>
        </div>
    );
};

export default UnifiedOnboardingWidget;
